import React, {
  useRef,
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { videoUploader } from "../../../utils/Common/MediaUploader";
import { getSessionStorageItem } from "../../../utils/SessionStorageMethod/getSessionStorageItem";
import { setSessionStorageItem } from "../../../utils/SessionStorageMethod/setSessionStorageItem";
import {
  AudioBooleanSettings,
  AudioSettings,
  VideoFrameRate,
  VideoResolution,
} from "../../../utils/Enums/AudioVideoSettings";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import { showToast } from "ui-components";
import { insertAIVisualAnalysis } from "../../../service/SubmitAssessment/SubmitAssessment";
interface Recorder {
  SpeechRecognition?: any;
  timer?: number;
  currentQuestionIndex?: number;
  questionId?: number | string;
  setVideoUrl?: any;
  isCodingQuestion?: boolean;
  data: any;
  handleSubmitAnswer?: any;
  setStartQuestionLoader: any;
  startQuestionLoader: boolean;
  isStartRecording: boolean;
  cameraStopped: boolean;
}
const Recorder: React.ForwardRefRenderFunction<any, Recorder> = (
  {
    SpeechRecognition,
    timer,
    currentQuestionIndex,
    questionId,
    isCodingQuestion = false,
    data,
    handleSubmitAnswer,
    setStartQuestionLoader,
    startQuestionLoader,
    isStartRecording,
    cameraStopped,
  },
  ref,
) => {
  const secondPageVideoRef = useRef<HTMLVideoElement | any | null>(null);
  const mediaRecorder = useRef<MediaRecorder | any>(null);
  const [recordingStopped, setRecordingStopped] = useState(false);
  const audioRecorder = useRef<MediaRecorder | any>(null);
  // const [currentEmotion, setCurrentEmotion] = useState("neutral");

  const faceLandmarkerRef = useRef<any | null>(null);
  const intervalRef = useRef<any>(null);
  const gazeCountsRef = useRef({
    left: 0,
    right: 0,
    up: 0,
    down: 0,
    total: 0,
    noFace: 0,
    multipleFaces: 0,
  });
  // function to get the expression in percentage
  const getEmotionAverages = (
    durations: Record<string, number>,
    currentExpression: string,
    expressionStart: number,
  ): { "Emotion Averages": Record<string, number> } => {
    const now = Date.now();
    const currentDuration = now - expressionStart;
    const durationsCopy = { ...durations };
    // Add current active expression time
    if (durationsCopy[currentExpression.toUpperCase()] !== undefined) {
      durationsCopy[currentExpression.toUpperCase()] += currentDuration;
    }
    const total = Object.values(durationsCopy).reduce((a, b) => a + b, 0);

    const rawPercentages: Record<string, number> = {};
    let sum = 0;

    for (const key in durationsCopy) {
      const percent = total ? (durationsCopy[key] / total) * 100 : 0;
      rawPercentages[key] = percent;
      sum += percent;
    }

    // ✅ Normalize so they always sum exactly to 100
    const percentages: Record<string, number> = {};
    const diff = 100 - sum;

    for (const key in rawPercentages) {
      // Round to 2 decimals
      percentages[key] = +rawPercentages[key].toFixed(2);
    }

    // Fix any rounding difference (assign to the largest emotion)
    if (Math.abs(diff) > 0.01) {
      const maxKey = Object.keys(percentages).reduce((a, b) =>
        percentages[a] > percentages[b] ? a : b,
      );
      percentages[maxKey] = +(percentages[maxKey] + diff).toFixed(2);
    }
    return { "Emotion Averages": percentages };
  };
  const expressionDurationsRef = useRef<Record<string, number>>({
    ANGRY: 0,
    CALM: 0,
    DISGUSTED: 0,
    FEAR: 0,
    HAPPY: 0,
    SAD: 0,
    SURPRISED: 0,
    CONFUSED: 0,
    NEUTRAL: 0,
    SMIRK: 0,
  });
  const insertAIVisualAnalysisApi = async (
    companyId: any,
    assessmentId: any,
    applicantId: any,
    questionId: any,
  ) => {
    const payload = {
      company_id: companyId,
      assessment_id: assessmentId,
      applicant_id: applicantId,
      question_id: questionId,
      result: {
        face_emotions_cont: getEmotionAverages(
          expressionDurationsRef.current,
          currentExpressionRef.current,
          expressionStartTimeRef.current,
        ),
      },
    };
    await insertAIVisualAnalysis(payload);
  };
  useImperativeHandle(ref, () => ({
    insertAIVisualAnalysisApi,
  }));
  const currentExpressionRef = useRef<string>("NEUTRAL");
  const expressionStartTimeRef = useRef<number>(Date.now());
  const setupCamera = async () => {
    try {
      // Define media constraints with defaults for better compatibility
      const audioConstraints = startQuestionLoader
        ? false
        : {
            sampleRate: {
              ideal: AudioSettings.SampleRateIdeal || 44100,
            },
            sampleSize: {
              ideal: AudioSettings.SampleSizeIdeal || 16,
            },
            channelCount: {
              ideal: AudioSettings.ChannelCountIdeal || 1,
            },
            echoCancellation: AudioBooleanSettings.EchoCancellation ?? true,
            noiseSuppression: AudioBooleanSettings.NoiseSuppression ?? true,
            autoGainControl: AudioBooleanSettings.AutoGainControl ?? true,
          };

      const videoConstraints = startQuestionLoader
        ? false
        : {
            width: {
              ideal: VideoResolution.WidthIdeal || 1280,
              max: VideoResolution.WidthMax || 1920,
            },
            height: {
              ideal: VideoResolution.HeightIdeal || 720,
              max: VideoResolution.HeightMax || 1080,
            },
            frameRate: {
              ideal: VideoFrameRate.FrameRateIdeal || 30,
              max: VideoFrameRate.FrameRateMax || 60,
            },
            facingMode: "user",
          };

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
        video: videoConstraints,
      });

      const setupFaceLandmarker = async () => {
        const filesetResolver = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm",
        );

        const faceLandmarker = await FaceLandmarker.createFromOptions(
          filesetResolver,
          {
            baseOptions: {
              // we also have cdn of it
              // modelAssetPath:
              // "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
              modelAssetPath: "/mediapipe/models/face_landmarker.task",
              delegate: "GPU",
            },
            outputFaceBlendshapes: true,
            runningMode: "VIDEO",
            numFaces: 2,
          },
        );

        faceLandmarkerRef.current = faceLandmarker;

        if (secondPageVideoRef.current) {
          secondPageVideoRef.current.srcObject = stream;
        }

        secondPageVideoRef.current.onloadedmetadata = () => {
          secondPageVideoRef.current.play();
          startTracking();
        };
      };

      const startTracking = () => {
        intervalRef.current = setInterval(() => {
          if (
            faceLandmarkerRef.current &&
            secondPageVideoRef.current &&
            !secondPageVideoRef.current.paused &&
            !secondPageVideoRef.current.ended
          ) {
            let results;
            try {
              results = faceLandmarkerRef.current.detectForVideo(
                secondPageVideoRef.current,
                performance.now(),
              );
            } catch (err) {
              console.warn("FaceLandmarker failed on Safari:", err);
              return;
            }
            const faces = results?.faceLandmarks;
            const blendshapes = results?.faceBlendshapes?.[0]?.categories;
            const emotion = getExpression(blendshapes);
            const now = Date.now();
            const prevExpression = currentExpressionRef.current;
            const prevStartTime = expressionStartTimeRef.current;
            const duration = now - prevStartTime;
            if (
              expressionDurationsRef.current[prevExpression.toUpperCase()] !==
              undefined
            ) {
              expressionDurationsRef.current[prevExpression.toUpperCase()] +=
                duration;
            }
            currentExpressionRef.current = emotion.toUpperCase();
            expressionStartTimeRef.current = now;
            if (!faces || faces.length === 0) {
              console.warn("No face detected.");
              gazeCountsRef.current.noFace += 1;
              gazeCountsRef.current.total += 1;
              if (gazeCountsRef.current.noFace >= 5) {
                showToast(
                  "No face detected",
                  "info",
                  100,
                  () => {},
                  "top-center",
                );
                gazeCountsRef.current.noFace = 0;
                gazeCountsRef.current.down = 0;
                gazeCountsRef.current.up = 0;
                gazeCountsRef.current.left = 0;
                gazeCountsRef.current.multipleFaces = 0;
                gazeCountsRef.current.total = 0;
                gazeCountsRef.current.right = 0;
              }
              return; // Exit early
            }

            if (faces.length > 1) {
              gazeCountsRef.current.multipleFaces += 1;
              gazeCountsRef.current.total += 1;
              console.warn("Multiple faces detected.");
              if (faces.length >= 1) {
                showToast(
                  "Mutiple Face detected",
                  "info",
                  100,
                  () => {},
                  "top-center",
                );
                gazeCountsRef.current.noFace = 0;
                gazeCountsRef.current.down = 0;
                gazeCountsRef.current.up = 0;
                gazeCountsRef.current.left = 0;
                gazeCountsRef.current.multipleFaces = 0;
                gazeCountsRef.current.total = 0;
                gazeCountsRef.current.right = 0;
              }
              return;
            }

            if (faces.length > 0) {
              const landmarks = faces[0];
              const leftIris = landmarks[468];
              const leftEye = landmarks[33];
              const rightEye = landmarks[133];
              const eyeTop = landmarks[159];
              const eyeBottom = landmarks[145];

              const gaze = getGazeDirectionRelative(leftIris, {
                left: leftEye,
                right: rightEye,
                top: eyeTop,
                bottom: eyeBottom,
              });

              // Update counters
              if (["left", "right", "up"].includes(gaze)) {
                if (gaze in gazeCountsRef.current) {
                  if (gaze !== "center") {
                    gazeCountsRef.current[gaze] += 1;
                    gazeCountsRef.current.total += 1;
                  }
                }
              }
              if (gaze != "center") {
                if (gazeCountsRef.current[gaze] >= 5) {
                  showToast(
                    "Please Look in center",
                    "info",
                    200,
                    () => {},
                    "top-center",
                  );
                  gazeCountsRef.current.noFace = 0;
                  gazeCountsRef.current.down = 0;
                  gazeCountsRef.current.up = 0;
                  gazeCountsRef.current.left = 0;
                  gazeCountsRef.current.multipleFaces = 0;
                  gazeCountsRef.current.total = 0;
                  gazeCountsRef.current.right = 0;
                }
              }
            }
          }
        }, 1000);
      };
      setupFaceLandmarker();
      if (!mediaRecorder.current) {
        mediaRecorder.current = new MediaRecorder(stream);
      }
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length > 0) {
        const audioStream = new MediaStream([audioTracks[0]]);
        audioRecorder.current = new MediaRecorder(audioStream);
      }

      if (mediaRecorder.current?.state === "inactive" && isStartRecording) {
        mediaRecorder.current.start();
        if (audioRecorder.current?.state === "inactive") {
          audioRecorder.current.start(); // ✅ Start audio recording
        }
        setRecordingStopped(true);
        SpeechRecognition.startListening({ continuous: true });
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
    }
  };
  useEffect(() => {
    if (cameraStopped) {
      stopCamera();
      stopRecording();
    }
  }, [cameraStopped]);
  useEffect(() => {
    setupCamera();
    return () => {
      clearInterval(intervalRef.current);
      if (
        mediaRecorder.current &&
        mediaRecorder?.current?.state === "recording"
      ) {
        mediaRecorder.current.stop();
      }
      if (
        audioRecorder.current &&
        audioRecorder?.current?.state === "recording"
      ) {
        audioRecorder.current.stop();
      }
    };
  }, [currentQuestionIndex, questionId, isStartRecording]);

  useEffect(() => {
    const checkMediaDevices = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: startQuestionLoader ? false : true,
          video: startQuestionLoader ? false : true,
        });

        // Stop the stream to release resources
        stream.getTracks().forEach((track) => track.stop());

        if (timer === 0) {
          await stopRecording();
          stopCamera();
        }
      } catch (error) {
        console.error(error);
      }
    };
    checkMediaDevices();
  }, [timer, recordingStopped]);

  const getGazeDirectionRelative = (iris: any, eyeBox: any) => {
    const xMin = eyeBox.left.x;
    const xMax = eyeBox.right.x;
    const yMin = eyeBox.top.y;
    const yMax = eyeBox.bottom.y;

    const xRatio = (iris.x - xMin) / (xMax - xMin);
    const yRatio = (iris.y - yMin) / (yMax - yMin);

    // Widened the center zone by adjusting thresholds
    const xThresholdRight = 0.25; // More tolerant for right movement
    const xThresholdLeft = 0.65; // More tolerant for left movement
    const yThresholdDown = 0.38; // More tolerant for downward movement
    const yThresholdUp = 0.45; // More tolerant for upward movement

    if (xRatio < xThresholdRight) return "right";
    if (xRatio > xThresholdLeft) return "left";
    if (yRatio < yThresholdDown) return "down";
    if (yRatio > yThresholdUp) return "up";

    return "center";
  };

  const stopCamera = () => {
    const stream = secondPageVideoRef.current?.srcObject as MediaStream;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      secondPageVideoRef.current!.srcObject = null;
    }
  };
  const stopRecording = async () => {
    return new Promise<void>((resolve) => {
      const chunks: Blob[] = [];
      const audioChunks: Blob[] = [];
      mediaRecorder.current.ondataavailable = (event: any) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      audioRecorder.current.ondataavailable = (event: any) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      // ✅ Only when both recorders stop
      let mediaStopped = false;
      let audioStopped = false;

      const maybeFinish = async () => {
        if (!mediaStopped || !audioStopped) return;

        const blob = new Blob(chunks, { type: "video/webm" });
        const audioBlob = new Blob(audioChunks, { type: "audio/webm" });
        const skipStatus = getSessionStorageItem("isStatusUpdate");
        const isReportStatus = getSessionStorageItem("isReport");
        if (
          !isReportStatus &&
          skipStatus !== "skipped" &&
          data?.type !== "Coding"
        ) {
          setStartQuestionLoader(true);
          // upload video
          const videoS3Uploaded = await videoUploader(
            blob,
            `${data?.mediaPath}${questionId}`,
          );
          // just Upload audio to s3
          await videoUploader(
            audioBlob,
            `${data?.mediaPath}${questionId}/${questionId}`,
          );
          handleSubmitAnswer(videoS3Uploaded?.fileName);
        } else {
          handleSubmitAnswer("");
          setSessionStorageItem("isReport", false);
        }
        resolve();
      };

      mediaRecorder.current.onstop = async () => {
        mediaStopped = true;
        maybeFinish();
      };

      audioRecorder.current.onstop = () => {
        audioStopped = true;
        maybeFinish();
      };

      mediaRecorder.current?.stop();
      audioRecorder.current?.stop();
      setRecordingStopped(false);
    });
  };

  const getExpression = (blendshapes: any[]): string => {
    if (!blendshapes || blendshapes.length === 0) return "calm";

    const scores: Record<string, number> = {};
    for (const shape of blendshapes) {
      scores[shape.categoryName] = shape.score;
    }

    const is = (key: string, threshold: number) =>
      (scores[key] ?? 0) > threshold;

    // Adjusted keys to match your actual blendshape data
    if (is("mouthSmileLeft", 0.3) && is("mouthSmileRight", 0.3)) {
      return "happy";
    }

    // 😏 Smirk: asymmetric smile
    if (
      (is("mouthSmileLeft", 0.2) && !is("mouthSmileRight", 0.1)) ||
      (is("mouthSmileRight", 0.2) && !is("mouthSmileLeft", 0.1))
    ) {
      return "smirk";
    }

    if (is("browDownLeft", 0.4) && is("browDownRight", 0.4)) {
      return "angry";
    }

    if (
      is("jawOpen", 0.2) &&
      is("eyeSquintLeft", 0.3) &&
      is("eyeSquintRight", 0.3)
    ) {
      return "surprised";
    }

    if (is("mouthFrownLeft", 0.2) && is("mouthFrownRight", 0.2)) {
      return "sad";
    }

    if (
      (is("eyeWideLeft", 0.4) || is("eyeWideRight", 0.4)) &&
      is("jawOpen", 0.3)
    ) {
      return "fear";
    }

    if (is("noseSneerLeft", 0.3) || is("noseSneerRight", 0.3)) {
      return "disgusted";
    }

    // 😕 Confused: one brow up and one brow down
    if (
      (is("browOuterUpLeft", 0.3) && is("browDownRight", 0.3)) ||
      (is("browOuterUpRight", 0.3) && is("browDownLeft", 0.3))
    ) {
      return "confused";
    }

    return "calm";
  };
  return (
    <>
      {/* START we will remove this code in the future after testing */}
      {/* <div
        id="gaze-stats"
        style={{
          position: "fixed",
          top: "10px",
          left: "10px",
          background: "rgba(0,0,0,0.7)",
          color: "#fff",
          padding: "12px 16px",
          borderRadius: "12px",
          fontSize: "14px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          zIndex: 9999,
        }}
      >
        <div>
          👁️ <strong>Gaze status</strong>
        </div>
        <div>
          Left: <span id="left">{gazeCountsRef.current.left}</span>
        </div>
        <div>
          Right: <span id="right">{gazeCountsRef.current.right}</span>
        </div>
        <div>
          Up: <span id="up">{gazeCountsRef.current.up}</span>
        </div>
        <div>
          Down: <span id="down">{gazeCountsRef.current.down}</span>
        </div>
        <div>
          No Face: <span id="noFace">{gazeCountsRef.current.noFace}</span>
        </div>
        <div>
          Multiple Faces:{" "}
          <span id="multipleFaces">{gazeCountsRef.current.multipleFaces}</span>
        </div>
        <div>
          Total: <span id="total">{gazeCountsRef.current.total}</span>
        </div>
      </div> */}
      {/* END we will remove this code in the future after testing */}
      {isCodingQuestion ? (
        <video
          style={{ marginLeft: "28px" }}
          width={250}
          ref={secondPageVideoRef}
          muted
          autoPlay={true}
        />
      ) : (
        <video ref={secondPageVideoRef} muted autoPlay={true} />
      )}
    </>
  );
};

export default forwardRef(Recorder);
